import React, { useState } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { register } from "../../services/api";

export default function RegisterForm() {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [hasReadTerms, setHasReadTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSuccess(null);

    if (!acceptTerms) {
      setErrors({ terms: "You must read and accept the Terms and Conditions." });
      return;
    }

    const { first_name, last_name, email, phone_number, password } = form;

    setLoading(true);
    try {
      await register({
        first_name,
        last_name,
        email,
        phone_number,
        password,
      });
      setSuccess("Account created successfully! Redirecting...");
      localStorage.setItem("identifier", email);
      setTimeout(() => {
        window.location.href = "/login";
      }, 2000);
    } catch (err) {
      const errorData = err?.response?.data;
      if (errorData && typeof errorData === "object") {
        const formattedErrors = {};
        Object.keys(errorData).forEach((key) => {
          formattedErrors[key] = Array.isArray(errorData[key])
            ? errorData[key][0]
            : errorData[key];
        });
        setErrors(formattedErrors);
      } else {
        setErrors({ general: "Registration failed. Please try again." });
      }
    } finally {
      setLoading(false);
    }
  };

  // FULL TERMS & CONDITIONS RESTORED (from original DOCX)
  const hospitalWalletTerms = `
    <div class="space-y-4 text-sm leading-relaxed text-gray-700">
      <h3 class="text-xl font-bold text-gray-900 mb-4">TERMS AND CONDITIONS FOR HOSPITAL WALLET PLATFORM</h3>
      <p class="mb-4">These Terms and Conditions (“Terms”) govern the use of the <strong>Hospital Wallet Platform</strong> (“the Wallet”), a digital payment and deposit system operated by <strong>University College Hospital, Ibadan</strong> (“the Hospital”, “we”, “us”, or “our”).</p>
      <p class="mb-6">By registering, funding, or using the Wallet, you acknowledge that you have read, understood, and agreed to be bound by these Terms.</p>

      <h4 class="text-lg font-semibold text-gray-800 mb-2">1. DEFINITIONS</h4>
      <ul class="list-disc pl-6 mb-4 space-y-1">
        <li><strong>“Wallet”</strong> means the virtual payment account assigned to a patient or user for managing hospital-related payments, deposits, or refunds.</li>
        <li><strong>“User” or “You”</strong> means any individual or authorized representative who registers to use the Hospital Wallet.</li>
        <li><strong>“Next of Kin”</strong> refers to a person duly recorded in the patient’s medical file or registration record as their emergency contact.</li>
        <li><strong>“Unspent Balance”</strong> means the remaining funds in a Wallet after payment for hospital services has been completed.</li>
        <li><strong>“Letter of Administration”</strong> means an official document issued by a competent court or authority empowering a person to administer a deceased person’s estate.</li>
        <li><strong>“Medical Record”</strong> means the official documentation of a patient’s treatment, diagnosis, and personal details maintained by the Hospital.</li>
      </ul>

      <h4 class="text-lg font-semibold text-gray-800 mb-2">2. PURPOSE AND SCOPE</h4>
      <p class="mb-4">The Hospital Wallet is provided for the purpose of:</p>
      <ul class="list-disc pl-6 mb-4 space-y-1">
        <li>Facilitating seamless payment for medical consultations, laboratory tests, admissions, drugs, and other hospital-related services;</li>
        <li>Enabling patients or their sponsors to pre-fund their healthcare expenses;</li>
        <li>Providing a secure channel for managing deposits and refunds in accordance with hospital policy.</li>
      </ul>
      <p class="mb-6">The Wallet does <strong>not</strong> function as a conventional bank account, savings account, or interest-bearing facility.</p>

      <h4 class="text-lg font-semibold text-gray-800 mb-2">3. ACCOUNT CREATION AND ACCESS</h4>
      <ul class="list-disc pl-6 mb-4 space-y-1">
        <li>To access the Wallet, users must complete registration through the hospital’s approved portal or registration desk.</li>
        <li>The Wallet shall be linked to the patient’s hospital number and personal or sponsor details.</li>
        <li>Each User is responsible for maintaining the confidentiality of their login credentials and ensuring that no unauthorized person has access to the Wallet.</li>
        <li>The Hospital reserves the right to suspend or deactivate any Wallet account in cases of suspected fraud, data inconsistency, or misuse.</li>
      </ul>

      <h4 class="text-lg font-semibold text-gray-800 mb-2">4. FUNDING OF WALLET</h4>
      <ul class="list-disc pl-6 mb-4 space-y-1">
        <li>Wallets may be funded via bank transfer, POS, debit/credit card, or other hospital-approved payment channels.</li>
        <li>All payments must originate from a verified bank account bearing the name of the patient or their registered sponsor.</li>
        <li>Any third-party funding not traceable to the patient’s record may be subject to additional verification or delay.</li>
      </ul>

      <h4 class="text-lg font-semibold text-gray-800 mb-2">5. USE OF WALLET FUNDS</h4>
      <ul class="list-disc pl-6 mb-4 space-y-1">
        <li>Funds in the Wallet may be used only for payments for hospital services, including consultation, treatment, admission, medication, diagnostics, and other approved purposes.</li>
        <li>Wallet balances are non-transferable and cannot be used for cash withdrawals.</li>
        <li>The Wallet shall not accrue interest, dividends, or other financial returns.</li>
        <li>The Hospital shall not be liable for unauthorized transactions resulting from the User’s negligence, disclosure of credentials, or unauthorized access.</li>
      </ul>

      <h4 class="text-lg font-semibold text-gray-800 mb-2">6. REFUND POLICY</h4>
      <p class="mb-2 font-medium">a. Refund of Unspent Balance</p>
      <ol class="list-decimal pl-6 mb-4 space-y-1">
        <li>Refunds for unspent balances shall be made only upon written request by the account holder or authorized representative.</li>
        <li>Refunds can only be paid into the same bank account that originally funded the Wallet.</li>
        <li>Refund requests shall be subject to verification of transaction history, identity, and compliance with hospital accounting procedures.</li>
        <li>Processing of refunds may take up to 10–15 working days after receipt of complete documentation.</li>
        <li>The Hospital reserves the right to deduct applicable administrative or processing fees before refund disbursement.</li>
      </ol>
      <p class="mb-2 font-medium">b. Non-Refundable Transactions</p>
      <ol class="list-decimal pl-6 mb-6 space-y-1">
        <li>Payments already utilized for services rendered are non-refundable.</li>
        <li>The Hospital shall not be responsible for refunds arising from user error, duplicate payments, or cancelled appointments, except as expressly approved by management.</li>
      </ol>

      <h4 class="text-lg font-semibold text-gray-800 mb-2">7. REFUND IN CASE OF DEATH</h4>
      <ol class="list-decimal pl-6 mb-6 space-y-2">
        <li>In the event of the death of a patient or Wallet holder, refund of any unspent balance shall be made only to:</li>
        <ul class="list-disc pl-8 space-y-1">
          <li>The documented <strong>Next of Kin</strong> as recorded in the deceased’s medical record, upon presentation of:
            <ul class="list-circle pl-6">
              <li>valid means of identification; and</li>
              <li>official proof of death (death certificate or hospital confirmation);</li>
            </ul>
          </li>
          <li>A person duly authorized under a <strong>Letter of Administration</strong> for the deceased’s estate, accompanied by a valid ID card and any additional documents reasonably required by the Hospital.</li>
        </ul>
        <li>The Hospital reserves the right to verify all submitted documents with relevant authorities before processing any refund.</li>
        <li>The Hospital shall not be liable for any dispute or claim arising between relatives, beneficiaries, or third parties regarding entitlement to the refund.</li>
        <li>Refunds under this clause shall be made strictly through official hospital channels and not in cash.</li>
      </ol>

      <h4 class="text-lg font-semibold text-gray-800 mb-2">8. DISPUTE RESOLUTION ON TRANSACTIONS</h4>
      <ul class="list-disc pl-6 mb-6 space-y-1">
        <li>Any dispute concerning Wallet transactions or balances must be reported within 7 working days from the date of occurrence.</li>
        <li>The Hospital will investigate the issue and provide feedback within a reasonable period.</li>
        <li>The Hospital’s liability for verified transaction errors shall not exceed the amount in dispute.</li>
      </ul>

      <h4 class="text-lg font-semibold text-gray-800 mb-2">9. DATA PRIVACY AND SECURITY</h4>
      <ul class="list-disc pl-6 mb-6 space-y-1">
        <li>The Hospital shall process and store all patient and transaction data in compliance with the <strong>Nigeria Data Protection Act 2023</strong> and other applicable data protection regulations.</li>
        <li>Your personal and medical information shall be kept confidential and used solely for the purpose of service delivery, internal audits, and regulatory compliance.</li>
        <li>By using the Wallet, you consent to the Hospital’s processing of your data in accordance with its Privacy Policy.</li>
        <li>The Hospital shall not be responsible for breaches resulting from third-party systems, internet providers, or force majeure events.</li>
      </ul>

      <h4 class="text-lg font-semibold text-gray-800 mb-2">10. SUSPENSION OR TERMINATION OF WALLET</h4>
      <ul class="list-disc pl-6 mb-6 space-y-1">
        <li>The Hospital may suspend or close a Wallet account if:</li>
        <ul class="list-circle pl-6">
          <li>fraudulent or suspicious activity is detected;</li>
          <li>required by law, regulation, or internal audit;</li>
          <li>the account remains dormant beyond a stipulated period.</li>
        </ul>
        <li>Upon termination, any unspent funds shall be refunded in accordance with Clauses 6 or 7 above.</li>
      </ul>

      <h4 class="text-lg font-semibold text-gray-800 mb-2">11. LIMITATION OF LIABILITY</h4>
      <ul class="list-disc pl-6 mb-6 space-y-1">
        <li>The Hospital shall not be liable for:</li>
        <ul class="list-circle pl-6">
          <li>any loss or damage arising from network failure, downtime, or payment gateway issues;</li>
          <li>unauthorized access caused by the User’s negligence;</li>
          <li>indirect, incidental, or consequential damages arising from use or inability to use the Wallet.</li>
        </ul>
        <li>The Hospital’s total liability shall not exceed the unspent Wallet balance at the time of claim.</li>
      </ul>

      <h4 class="text-lg font-semibold text-gray-800 mb-2">12. GOVERNING LAW AND JURISDICTION</h4>
      <p class="mb-6">These Terms shall be governed by and construed in accordance with the laws of the <strong>Federal Republic of Nigeria</strong>. Any dispute arising hereunder shall first be resolved amicably; failing which, it shall be referred to arbitration in <strong>Lagos State</strong>, in accordance with the <em>Arbitration and Conciliation Act, Cap A18, LFN 2004</em>.</p>

      <h4 class="text-lg font-semibold text-gray-800 mb-2">13. COMPLIANCE</h4>
      <p class="mb-6">All Users agree to comply with all applicable laws, regulations, and hospital policies governing electronic payments, patient records, and financial transactions.</p>

      <h4 class="text-lg font-semibold text-gray-800 mb-2">14. AMENDMENTS</h4>
      <p class="mb-6">The Hospital reserves the right to modify these Terms at any time. Any updated version will be published on the Hospital’s official website or displayed at designated service points. Continued use of the Wallet after notice of amendment constitutes acceptance of the revised Terms.</p>

      <h4 class="text-lg font-semibold text-gray-800 mb-2">15. MISCELLANEOUS</h4>
      <ul class="list-disc pl-6 mb-6 space-y-1">
        <li>These Terms constitute the entire agreement between the User and the Hospital with respect to the Wallet service.</li>
        <li>If any provision of these Terms is found invalid or unenforceable, the remaining provisions shall remain in full force and effect.</li>
        <li>The Hospital’s failure to enforce any provision shall not constitute a waiver of that right.</li>
      </ul>

      <p class="mt-6 text-sm italic text-gray-600">By registering and using the Hospital Wallet, you acknowledge that you have read, understood, and agreed to these Terms and Conditions.</p>
    </div>
  `;

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-6 max-w-md mx-auto p-6 rounded-lg">
        {/* Form Fields */}
        <div>
          <input
            type="text"
            name="first_name"
            placeholder="First Name"
            className="w-full border border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={form.first_name}
            onChange={handleChange}
            required
          />
          {errors.first_name && <p className="text-red-600 text-sm mt-1">{errors.first_name}</p>}
        </div>

        <div>
          <input
            type="text"
            name="last_name"
            placeholder="Last Name"
            className="w-full border border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={form.last_name}
            onChange={handleChange}
            required
          />
          {errors.last_name && <p className="text-red-600 text-sm mt-1">{errors.last_name}</p>}
        </div>

        <div>
          <input
            type="email"
            name="email"
            placeholder="Email"
            className="w-full border border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={form.email}
            onChange={handleChange}
            required
          />
          {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email}</p>}
        </div>

        <div>
          <input
            type="tel"
            name="phone_number"
            placeholder="Phone Number"
            className="w-full border border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={form.phone_number}
            onChange={handleChange}
            required
          />
          {errors.phone_number && <p className="text-red-600 text-sm mt-1">{errors.phone_number}</p>}
        </div>

        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            placeholder="Password"
            className="w-full border border-gray-300 p-3 rounded-lg pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={form.password}
            onChange={handleChange}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
          >
            {showPassword ? <FaEyeSlash className="h-5 w-5" /> : <FaEye className="h-5 w-5" />}
          </button>
          {errors.password && <p className="text-red-600 text-sm mt-1">{errors.password}</p>}
        </div>

        {/* INSTRUCTION ABOVE CHECKBOX */}
        <p className="text-xs text-amber-800 font-medium mb-2 -mt-2">
          You must read the Terms and Conditions by clicking the link below before you can register.
        </p>

        {/* TERMS CHECKBOX - ONE LINE (UNCHANGED) */}
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="acceptTerms"
            checked={acceptTerms}
            disabled={!hasReadTerms}
            className={`h-4 w-4 rounded border-gray-300 ${
              hasReadTerms
                ? "text-blue-600 focus:ring-blue-500 cursor-pointer"
                : "text-gray-400 cursor-not-allowed"
            }`}
            readOnly
          />
          <label htmlFor="acceptTerms" className="text-sm text-gray-700 select-none">
            I have read and agree to the{" "}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setShowTermsModal(true);
              }}
              className="text-blue-600 hover:text-blue-800 underline font-medium"
            >
              Terms and Conditions
            </button>
          </label>
        </div>
        {errors.terms && <p className="text-red-600 text-sm mt-1 ml-7">{errors.terms}</p>}

        {errors.general && <p className="text-red-600 text-sm mt-2">{errors.general}</p>}
        {success && <p className="text-green-600 text-sm mt-2">{success}</p>}

        <button
          type="submit"
          className={`w-full py-3 rounded-lg text-lg font-semibold transition-all duration-300 ${
            loading || !acceptTerms
              ? "bg-gray-400 text-gray-200 cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-700"
          }`}
          disabled={loading || !acceptTerms}
        >
          {loading ? "Processing..." : "Sign Up"}
        </button>

        <p className="text-center text-blue-600 text-sm cursor-pointer mt-4">
          Already have an account? <a href="/login" className="underline hover:text-blue-800">Log in</a>
        </p>
      </form>

      {/* TERMS MODAL */}
      {showTermsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-xl font-bold text-gray-900">Hospital Wallet Terms & Conditions</h3>
              <button
                onClick={() => setShowTermsModal(false)}
                className="text-gray-400 hover:text-gray-600 text-3xl"
              >
                ×
              </button>
            </div>

            <div className="flex-1 p-6 overflow-y-auto prose prose-sm max-w-none">
              <div dangerouslySetInnerHTML={{ __html: hospitalWalletTerms }} />
            </div>

            <div className="p-6 border-t flex justify-end space-x-3">
              <button
                onClick={() => setShowTermsModal(false)}
                className="px-5 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setHasReadTerms(true);
                  setAcceptTerms(true);
                  setShowTermsModal(false);
                }}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                I Agree
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}